/*
 * @Author: changdou.fe@bytedance.com
 * @Date: 2025-07-17 22:36:05
 * @LastEditors: changdou.fe@bytedance.com
 * @LastEditTime: 2025-10-09 20:44:57
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary !text-font-reverse hover:bg-primary-hover shadow-lg',
        ['default-outline']: 'border border-primary/60 text-primary hover:bg-primary/10',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-button-border bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'border border-button-border bg-secondary text-secondary-foreground hover:bg-secondary-hover max-md:border-none',
        ['secondary-light']:
          'border border-button-border bg-background-card dark:bg-secondary/50 text-secondary-foreground hover:bg-secondary-hover  dark:hover:bg-secondary dark:border-button-border/85 max-md:border-none',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        rounded:
          'text-font-primary bg-background/50 hover:bg-background/100',
        link: 'text-primary underline-offset-4 hover:underline',
        text: 'text-font-primary underline-offset-4 hover:underline',
        green:
          'bg-green-500 text-font-reverse hover:opacity-80 disabled:cursor-not-allowed',
        ['outline-green']:
          'border border-button-border border-green-500 dark:bg-transparent dark:border-green-500 bg-white text-green-500 hover:bg-white hover:text-green-500/80 hover:border-green-500',
        blue: 'bg-blue text-font-reverse hover:opacity-80',
        danger: 'bg-red-500 text-font-reverse hover:opacity-80',
        ['danger-outline']: 'border border-red-500 text-red-500 hover:bg-red-500/10 hover:text-red-500',
        orange: 'bg-orange-500 text-font-reverse hover:opacity-80',
        white: 'bg-white text-black hover:bg-gray-100',
        gray: 'bg-gray-500 text-black hover:bg-gray-600 active:bg-gray-600',
        ['gray-disabled']: 'bg-gray-300 dark:bg-gray-400 dark:text-font-primary',
        ['outline-disabled']:
          'border border-button-border hover:bg-accent hover:text-accent-foreground',
        dex: 'border-none bg-[#f3f3f3] text-black hover:bg-[#d9d9d9] active:bg-[#d9d9d9] cursor-pointer dark:bg-[#1d1d1d] dark:text-font-primary dark:hover:bg-[#383838] dark:active:bg-[#383838]',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        smm: 'h-7 px-3 text-xs',
        lg: 'h-11 px-8',
        xs: 'h-6 px-2 gap-0 item-center text-xs',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
        {loading ? loadingText || children : children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
